import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import loginApi from "@/service/modules/loginApi";
import { matomoSetCustomDimension } from "@/utils/matomoEvent";
import { setCookie } from "@/utils/cookie";
import { updateUserInfo } from "@/store/slices/userInfoSlice";

let oneTapInitialized = false;

export function useGoogleOneTapPrompt() {
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.userInfo.data);

  const promptedRef = useRef(false);
  const userInfoRef = useRef(userInfo);

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  useEffect(() => {
    // 已登录，不需要 One Tap
    if (userInfo?.customerID) return;
    if (promptedRef.current || oneTapInitialized) return;
    if (!isChrome()) return;

    const googleCheckTimer = setInterval(() => {
      if ((window as any).google?.accounts?.id) {
        clearInterval(googleCheckTimer);

        oneTapInitialized = true;
        promptedRef.current = true;

        const clientId = getGoogleClientId();

        (window as any).google.accounts.id.initialize({
          client_id: clientId,
          callback: (googleRes: any) => handleGoogleLogin(googleRes, dispatch),
          cancel_on_tap_outside: false,
          auto_select: false,
        });

        // ⏳ 最多等 10 秒，每秒判断是否已登录
        let seconds = 0;
        const promptTimer = setInterval(() => {
          seconds++;

          // 期间如果用户已登录，直接终止
          if (userInfoRef.current?.customerID) {
            clearInterval(promptTimer);
            return;
          }

          if (seconds >= 10) {
            clearInterval(promptTimer);
            (window as any).google.accounts.id.prompt();
          }
        }, 1000);
      }
    }, 200);

    return () => {
      clearInterval(googleCheckTimer);
    };
  }, []);
}

async function handleGoogleLogin(googleRes: any, dispatch: any) {
  if (googleRes.credential) {
    try {
      const res = await loginApi.googleOneTapLogin({
        credential: googleRes.credential,
      });
      if (res.data) {
        if (res.data.userBehaviorType == 'login') {
          // matomo记录登录结果
          let loginResult = 0;
          if (window && (window as any)._paq) {
            (window as any)._paq.push(["setUserId", res.data.customerID]);
          }
          matomoSetCustomDimension(23, {loginMethod: 'gotLogin', loginResult});
        } else if (res.data.userBehaviorType == 'register'){
          // matomo记录注册结果
          let registerResult = 0
          matomoSetCustomDimension(24, {registerMethod: 'gotRegister', registerResult})
        }

        // userEmail存起来，方便数据埋点使用
        if (res.data.email) {
          localStorage.setItem("userEmail", res.data.email || "");
          setCookie("userCustomerId", res.data.customerID || "");
        } else {
          localStorage.removeItem("userEmail");
        }
        // 登录成功，保存用户信息到redux
        dispatch(updateUserInfo(res.data));
      }
    } catch (error: any) {
      console.error("Google One Tap Login Error:", error);
      // matomo 记录登录失败信息
      let loginResult = error.code;
      matomoSetCustomDimension(23, {loginMethod: 'gotLogin', loginResult});
    }
  }
}

function getGoogleClientId() {
  const hostname = window.location.hostname;
  if (hostname === "www.bestdeals.co.nz") {
    return "1016734142754-534vrv7aj6o7b4qi1mh3f4q9h4p1eruc.apps.googleusercontent.com";
  }
  return "612106511487-5dm0fk17ggn1n39j218njc5s58t4rppq.apps.googleusercontent.com";
}

function isChrome() {
  if (typeof window === "undefined") return false;
  return (
    /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
  );
}
